import { SearchMatchType } from '@aibees/elephant/types';
import { PaginationResponse } from '@aibees/shared/definition';
import { CompanyTableInfo } from '../types';

export type Operator = 'all' | 'any';

export type InclusionMode = 'include' | 'exclude';

export type RangeType = 'RANGE' | 'ALL' | 'NONE';

export type Inclusion = {
  include?: string[] | InclusionWithSearchMatchType;
  exclude?: string[] | InclusionWithSearchMatchType;
};

export type InclusionWithSearchMatchType = {
  mode: SearchMatchType;
  content: string[];
};

export type InclusionQuery = {
  all?: Inclusion;
  any?: Inclusion;
};

export type InclusionWithSourceQuery = {
  all?: {
    include?: {
      sources?: { mode: SearchMatchType; source: string }[];
      content?: string[];
    };
    exclude?: {
      sources?: { mode: SearchMatchType; source: string }[];
      content?: string[];
    };
  };
  any?: {
    include?: {
      sources?: { mode: SearchMatchType; source: string }[];
      content?: string[];
    };
    exclude?: {
      sources?: { mode: SearchMatchType; source: string }[];
      content?: string[];
    };
  };
};

/* export type InclusionWithSourceQuery = Record<
  Operator,
  { include?: { contents?: string[]; sources?: string[] } }
>; */

export type RangeValue = { start?: number; end?: number };

export type RangeQuery = {
  type?: RangeType;
  range?: RangeValue | RangeValue[];
};

export type InclusionQueries = {
  id?: InclusionQuery;
  name?: InclusionQuery;
  nameOrDomain?: InclusionQuery;
  domain?: InclusionQuery;
  fullName?: InclusionQuery;
  socialMediaLink?: InclusionQuery;
  phoneNumber?: InclusionQuery;
  email?: InclusionQuery;
  companyHQ?: InclusionQuery;
  industries?: InclusionQuery;
  socialMedia?: InclusionQuery;
  technology?: InclusionQuery;
  technologies?: InclusionQuery;
  sicCode?: InclusionQuery;
  naics?: InclusionQuery;
  fundingType?: InclusionQuery;
  language?: InclusionQuery;
  seniority?: InclusionQuery;
  location?: InclusionQuery;
  locations?: InclusionQuery;
  contactLocation?: InclusionQuery;
  companyLocation?: InclusionQuery;
  companySocialMedia?: InclusionQuery;
  contactSocialMedia?: InclusionQuery;
  departmentAndFunction?: InclusionQuery;
  relevancy?: InclusionQuery;
  label?: InclusionQuery;
  user?: InclusionQuery;
  type?: InclusionQuery;
  productAndServices?: InclusionQuery;
  skill?: InclusionQuery;
  certification?: InclusionQuery;
  maxPeoplePerCompany?: InclusionQuery;
  export?: InclusionQuery;
  enrichment?: InclusionQuery;
  company?: {
    current?: InclusionQuery;
    previous?: InclusionQuery;
    latest?: InclusionQuery;
  };
  education?: {
    school?: InclusionQuery;
    degree?: InclusionQuery;
    fieldOfStudy?: InclusionQuery;
    date?: RangeValue;
  };
  profileBadge?: InclusionQuery;
  url?: InclusionQuery;
  linkedin?: InclusionQuery;
  searchList?: {
    companyId?: InclusionQuery;
    personLatestCompanyId?: InclusionQuery;
    companyLinkedin?: InclusionQuery;
    companyDomain?: InclusionQuery;
    companyName?: InclusionQuery;
    personId?: InclusionQuery;
    personLinkedin?: InclusionQuery;
  };
};

export enum InclusionQueryWithSeachMatchFields {
  NAME = 'name',
  FULL_NAME = 'fullName',
  PRODUCT_AND_SERVICES = 'productAndServices',
  SKILL = 'skill',
  CERTIFICATION = 'certification',
  LANGUAGE = 'language',
  INDUSTRIES = 'industries',
  TECHNOLOGIES = 'technologies',
  LOCATIONS = 'locations',
}

// Type-safe way to get InclusionQuery values from the enum
export type InclusionQueryWithSeachMatchFieldValues = keyof Pick<
  InclusionQueries,
  (typeof InclusionQueryWithSeachMatchFields)[keyof typeof InclusionQueryWithSeachMatchFields]
>;

export type InclusionWithSourceQueries = {
  keyword?: InclusionWithSourceQuery;
};

export type RangeQueries = {
  employeeSize?: RangeQuery;
  revenue?: RangeQuery;
  foundedYear?: RangeQuery;
  language?: RangeQueries;
  retailSize?: RangeQuery;
};

export type FundingQueries = {
  funding?: {
    type?: string[]; // TODO: replace with funding type enum
    notReceived?: boolean;
    totalAmount?: RangeValue;
    lastAmount?: RangeValue;
    duration?: RangeValue;
  };
};

export type GeoLocationQuery = {
  geoLocation?: {
    position: { lat: number; lng: number };
    radius: number;
    unit: 'Kilometers' | 'Miles';
  };
};

export type DurationValue = { year?: number; month?: number };

export type JobExperienceQuery = {
  experience?: {
    latest?: {
      title?: InclusionQuery;
    };
    current?: {
      title?: InclusionQuery;
      duration?: {
        currentJob?: { min?: DurationValue; max?: DurationValue };
        currentCompany?: { min?: DurationValue; max?: DurationValue };
        total?: { min?: DurationValue; max?: DurationValue };
      };
    };
    previous?: {
      title?: InclusionQuery;
    };
  };
};

export type MetricQueryOption =
  | 'INCLUDE'
  | 'INCLUDE_AND'
  | 'EXCLUDE'
  | 'EXCLUDE_AND';

export type MetricDepartmentItem = {
  function: string[];
  start?: number;
  end?: number;
  option: MetricQueryOption;
};

export type MetricGrowthItem = {
  function: string[];
  start?: number;
  end?: number;
  timeFrame: string;
  option: MetricQueryOption;
};

// `metric` (singular) is the key both elephant and the public API accept.
// Elephant also binds a legacy `metrics` alias, the public API does not — see
// docs/copy-apply-search-payload.md §6.2.2.
export type MetricQuery = {
  metric?: {
    employee?: MetricDepartmentItem[];
    growth?: MetricGrowthItem[];
  };
};

// Company-search "Job Role" filter payload, nested under account.employee.
// Mirrors the sample contract: { title, seniority, departmentAndFunction }.
export type EmployeeQuery = {
  employee?: {
    title?: InclusionQuery;
    seniority?: InclusionQuery;
    departmentAndFunction?: InclusionQuery;
  };
};

export type SocialMediaFollowerPlatform = 'linkedin';

export type SocialMediaFollowerKind = 'followers' | 'connections';

export type SocialMediaFollowerQueries = {
  socialMediaFollower?: {
    [P in SocialMediaFollowerPlatform]?: {
      [K in SocialMediaFollowerKind]?: RangeQuery;
    };
  };
};

export type CompanySearchBody = CompanySearchBodyQueries & {
  type: CompanyRequestType;
  modality?: SearchModality;
  value?: string[];
};

export enum CompanyRequestType {
  COMPANY = 'COMPANY',
  SIMILARITY = 'SIMILARITY',
  SEMANTIC = 'SEMANTIC',
}

export type CompanySearchBodyQueries = {
  [key in QueryTarget]: InclusionQueries &
    InclusionWithSourceQueries &
    RangeQueries &
    FundingQueries &
    GeoLocationQuery &
    JobExperienceQuery &
    MetricQuery &
    EmployeeQuery &
    SocialMediaFollowerQueries; // & LanguageQueries;
};

export enum QueryTarget {
  contact = 'contact',
  account = 'account',
}

export type CompanySearchResponse = PaginationResponse<CompanyTableInfo>;

export enum KeywordSources {
  NAME = 'NAME',
  KEYWORD = 'KEYWORD',
  SEO = 'SEO',
  DESCRIPTION = 'DESCRIPTION',
  INDUSTRY = 'INDUSTRY',
}

export enum SearchModality {
  TOTAL = 'TOTAL',
  SAVED = 'SAVED',
  UNSAVED = 'UNSAVED',
}
